<template>
  <div>
    <Dialog
      :visible="visible && !siteFormVisible"
      header="New Project"
      :modal="true"
      :style="{ width: '680px' }"
      class="p-fluid"
      @update:visible="$emit('close')"
    >
      <p class="sp-sub" style="margin-top: 0;">
        A Project is one piece of work at a Site (a block, phase or building). Every contractor and every product type quoted for it
        sits under the same Project.
      </p>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col-12">
          <label for="spProjSite">Site *</label>
          <div class="p-inputgroup">
            <Dropdown
              id="spProjSite"
              v-model="siteId"
              :options="siteOptions"
              optionLabel="label"
              optionValue="id"
              :filter="true"
              filterPlaceholder="Type site name, street or postcode"
              placeholder="Pick the site"
            />
            <Button icon="pi pi-plus" label="Add site" class="p-button-warning" style="width: auto;" @click="siteFormVisible = true" />
          </div>
          <small v-if="submitted && !siteId" class="p-invalid">Site is required.</small>
        </div>
      </div>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col-12">
          <label for="spProjName">Project name *</label>
          <InputText id="spProjName" v-model.trim="name" style="text-transform: uppercase;" placeholder="e.g. BLOCK B, PHASE 2, ALFRED DENNY BUILDING" />
          <small class="sp-muted">Name the work, not the product: "SCREENS" or "IRONMONGERY" belong on the enquiry's product type.</small>
          <small v-if="submitted && !name" class="p-invalid"><br />Project name is required.</small>
        </div>
      </div>

      <div class="p-formgrid p-grid">
        <div class="p-field p-col-12">
          <label for="spProjNotes">Notes (optional)</label>
          <Textarea id="spProjNotes" v-model="notes" rows="2" :autoResize="true" />
        </div>
      </div>

      <div v-if="looksLikeProductType" class="sp-note warn">
        <i class="pi pi-exclamation-triangle"></i>
        <span>"{{ name }}" looks like a product type. If it is the same job as an existing project, use that project and set the product type on the enquiry.</span>
      </div>

      <div v-if="siteId && existing.length" :class="'sp-note ' + (strongMatch ? 'warn' : 'grey')" style="display: block;">
        <div>
          <i :class="strongMatch ? 'pi pi-exclamation-triangle' : 'pi pi-list'"></i>
          <strong>{{ strongMatch ? 'Possible duplicate project' : 'Projects already on this site' }}</strong>: is it one of these?
        </div>
        <table class="sp-table" style="margin-top: 6px; background: #fff;">
          <tr v-for="r in existing" :key="r.project.id">
            <td>
              <strong>{{ r.project.name }}</strong>
              <span v-if="r.sim >= 0.5" class="sp-chip High" style="margin-left: 6px;">very similar name</span>
              <br /><span class="sp-muted">{{ summary(r.project.id) }}</span>
            </td>
            <td style="width: 130px;"><Button label="Use this project" class="p-button-sm p-button-outlined" @click="$emit('picked', r.project)" /></td>
          </tr>
        </table>
      </div>

      <template #footer>
        <Button label="Cancel" icon="pi pi-times" class="p-button-danger" style="width: auto;" @click="$emit('close')" />
        <Button label="Save project" icon="pi pi-check" class="p-button-success" style="width: auto;" @click="save" />
      </template>
    </Dialog>

    <SiteForm
      v-if="siteFormVisible"
      :visible="siteFormVisible"
      @close="siteFormVisible = false"
      @picked="onSitePicked"
    />
  </div>
</template>

<script>
import SiteForm from './SiteForm.vue'
import { sites, projects, siteLabel, possibleDuplicateProjects, projectTotals, money, productTypes } from './mockData'

export default {
  name: 'ProjectForm',
  components: { SiteForm },
  props: { visible: Boolean, presetSiteId: { type: Number, default: null } },
  data() {
    return { siteId: this.presetSiteId, name: '', notes: '', submitted: false, siteFormVisible: false, siteVersion: 0 }
  },
  computed: {
    siteOptions() {
      this.siteVersion // re-read after a site is added in memory
      return sites.map(s => ({ id: s.id, label: siteLabel(s) }))
    },
    existing() { return possibleDuplicateProjects(this.siteId, this.name) },
    strongMatch() { return this.existing.some(r => r.sim >= 0.5) },
    looksLikeProductType() {
      const first = (this.name || '').toUpperCase().split(' ')[0]
      return first.length >= 4 && productTypes.some(t => t.toUpperCase().split(' ')[0] === first)
    }
  },
  methods: {
    summary(projectId) {
      const t = projectTotals(projectId)
      return t.nEnquiries + ' enquiries, ' + t.nCustomers + ' customer(s), ' + (t.productTypes.join(', ') || 'no product type yet') + ', counted ' + money(t.counted)
    },
    onSitePicked(site) {
      this.siteVersion++
      this.siteId = site.id
      this.siteFormVisible = false
    },
    save() {
      this.submitted = true
      if (!this.siteId || !this.name) return
      // Mock only: kept in memory until the page is reloaded.
      const created = { id: Date.now(), siteId: this.siteId, name: this.name.toUpperCase(), notes: this.notes, created: new Date().toLocaleDateString('en-GB') }
      projects.push(created)
      this.$emit('picked', created)
    }
  }
}
</script>

<style scoped>
@import './sitesProjects.css';
</style>
